import { useState } from 'react';

function PlatformsSection({ user }) {
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);

  const platforms = [
    {
      id: 'instagram',
      name: 'Instagram',
      icon: '📷',
      color: 'from-pink-500 to-purple-600',
      description: 'Connect to share photos and stories',
      features: ['Auto-post', 'Stories', 'Reels', 'Multi-image']
    },
    {
      id: 'twitter',
      name: 'Twitter',
      icon: '🐦',
      color: 'from-blue-400 to-cyan-500',
      description: 'Share quick updates and engage',
      features: ['Auto-tweet', 'Threads', 'Media', 'Polls']
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: '💼',
      color: 'from-blue-600 to-blue-800',
      description: 'Professional networking platform',
      features: ['Auto-post', 'Articles', 'Company page', 'Analytics']
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: '👥',
      color: 'from-blue-500 to-indigo-600',
      description: 'Connect with friends and pages',
      features: ['Auto-post', 'Stories', 'Groups', 'Events']
    }
  ];

  const handleConnect = (platformId) => {
    alert('OAuth connection will be implemented here.');
  };

  const handleDisconnect = (platformId) => {
    setConnectedPlatforms(connectedPlatforms.filter(p => p !== platformId));
    alert('Disconnected successfully!');
  };

  const isConnected = (platformId) => connectedPlatforms.includes(platformId);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold gradient-text mb-2">Social Media Connections</h2>
        <p style={{ color: 'var(--text-muted)' }}>Connect your social media accounts to publish directly</p>
      </div>

      <div className="theme-card border rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold accent-text mb-1">Connection Status</h3>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {connectedPlatforms.length} of {platforms.length} platforms connected
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold accent-text">
              {connectedPlatforms.length}/{platforms.length}
            </div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Connected</div>
          </div>
        </div>
        
        <div className="mt-4 w-full rounded-full h-2" style={{ backgroundColor: 'var(--border-color)' }}>
          <div 
            className="accent-gradient h-2 rounded-full transition-all duration-500"
            style={{ width: `${(connectedPlatforms.length / platforms.length) * 100}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {platforms.map((platform) => {
          const connected = isConnected(platform.id);
          return (
            <div
              key={platform.id}
              className="theme-card border-2 rounded-xl p-6 transition card-hover"
              style={{
                borderColor: connected ? '#10b981' : 'var(--border-color)'
              }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-16 h-16 bg-gradient-to-br ${platform.color} rounded-xl flex items-center justify-center text-3xl`}>
                    {platform.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold accent-text">{platform.name}</h3>
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{platform.description}</p>
                  </div>
                </div>
                {connected && (
                  <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-full">
                    ✓ Connected
                  </span>
                )}
              </div>

              <div className="mb-4">
                <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Features:</h4>
                <div className="flex flex-wrap gap-2">
                  {platform.features.map((feature, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs rounded"
                      style={{ backgroundColor: 'var(--secondary-bg)', color: 'var(--text-muted)' }}
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              {connected ? (
                <button
                  onClick={() => handleDisconnect(platform.id)}
                  className="w-full px-6 py-3 border rounded-lg font-medium transition"
                  style={{ backgroundColor: 'rgba(220, 38, 38, 0.2)', borderColor: '#ef4444', color: '#f87171' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(220, 38, 38, 0.3)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(220, 38, 38, 0.2)'}
                >
                  Disconnect
                </button>
              ) : (
                <button
                  onClick={() => handleConnect(platform.id)}
                  className="w-full px-6 py-3 accent-gradient text-white rounded-lg font-medium transition btn-glow"
                >
                  Connect {platform.name}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 theme-card-nested border rounded-xl p-6" style={{ borderColor: '#3b82f6', backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
        <h4 className="text-lg font-bold mb-3 text-blue-400">📱 About Platform Connections</h4>
        <div className="space-y-2 text-sm text-blue-300">
          <p>🔒 <strong>Secure OAuth:</strong> We use secure OAuth 2.0 authentication.</p>
          <p>📤 <strong>Auto-Publishing:</strong> Once connected, publish directly from MaxeDigital AI.</p>
          <p>🔄 <strong>Sync Status:</strong> Real-time synchronization with your accounts.</p>
        </div>
      </div>
    </div>
  );
}

export default PlatformsSection;
